import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { IconLoader2 } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { ThreadListCard } from '@/components/forum/ThreadListCard'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumCard,
  forumCardHeader,
  forumEmptyBox,
  forumKicker,
  forumLink,
  forumMutedSm,
  forumTitleMd,
  forumLoaderRow,
} from '@/lib/forumUi'
import type { ThreadListItem } from '@/lib/types'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function BookmarksPage() {
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()
  const [threads, setThreads] = useState<ThreadListItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (authLoading) return
    if (!me) {
      setLoading(false)
      return
    }
    let alive = true

    void apiFetch<{ threads: ThreadListItem[] }>(`/api/bookmarks`)
      .then(payload => {
        if (!alive) return
        setThreads(payload.threads ?? [])
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [me, authLoading])

  if (authLoading) {
    return (
      <div className={forumLoaderRow} aria-busy="true" aria-live="polite">
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Checking your session…
      </div>
    )
  }

  if (guest) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`} aria-labelledby="bookmarks-signed-out">
        <ForumSeo
          title={forumPageTitle('Bookmarks')}
          description={truncateForMetaDescription(
            'Saved NK Our Town discussions from North Kingstown RI neighbors.',
          )}
          noindex
        />
        <h1 id="bookmarks-signed-out" className={forumTitleMd}>
          Bookmarks
        </h1>
        <p className={`${forumMutedSm} mt-2 text-zinc-600`}>Saved threads are tied to your login.</p>
        <Link className="mt-5 inline-block" to="/sign-in">
          <Button>Sign in</Button>
        </Link>
      </section>
    )
  }

  if (!me) return null

  if (loading) {
    return (
      <div className={forumLoaderRow}>
        <IconLoader2 className="size-5 animate-spin shrink-0" aria-hidden="true" />
        Loading bookmarks…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ForumSeo
        title={forumPageTitle('Bookmarks')}
        description={truncateForMetaDescription(
          'Your saved NK Our Town discussions from North Kingstown RI neighbors.',
        )}
        noindex
      />
      <header className={forumCard}>
        <div className={forumCardHeader}>
          <p className={forumKicker}>Account</p>
          <h1 className={forumTitleMd}>Bookmarked threads</h1>
          <p className={`${forumMutedSm} mt-2 text-zinc-600`}>
            Topics you saved from the bookmarks control on a thread.
          </p>
        </div>
      </header>

      {threads.length === 0 ? (
        <div className={forumEmptyBox}>
          <p className="font-semibold text-zinc-950">No bookmarks yet.</p>
          <p className="mt-2 max-w-[62ch] text-[15px] leading-relaxed text-zinc-600">
            Open any thread and use the bookmarks control in the thread toolbar to keep it on this list.
          </p>
        </div>
      ) : (
        <div className={forumCard}>
          {threads.map((t, idx) => (
            <ThreadListCard key={t.id} thread={t} isLastRow={idx === threads.length - 1} />
          ))}
        </div>
      )}
    </div>
  )
}
